import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import {
  addDays,
  fetchDay,
  formatFullDate,
  formatTime,
  meridiem,
  today,
  toMinutes,
} from '../api/calendar'
import {
  assignColumns,
  buildLanes,
  fetchBookings,
  fetchConflicts,
  filterLanes,
  minutesOf,
  stretchGrid,
  type TeacherItem,
  type TeacherLane,
} from '../api/bookings'
import { withFilter } from '../useCalendarFilter'
import { bookingIsOff, lessonSessionOf, withoutShownPrivates } from '../api/privateLessons'
import CalendarViewSwitcher, { type CalendarView } from './CalendarViewSwitcher'
import CalendarDatePicker from './CalendarDatePicker'
import RosterModal from './RosterModal'

const ROW_HEIGHT = 22 // px per 15-minute slot, matching the room grid
const FALLBACK_COLOUR = 'var(--color-ink-3)'

/**
 * One commitment, positioned over the slots it occupies.
 *
 * Same construction as the room grid's block: CSS grid row spans rather than
 * pixel maths, so a private ending at 8:45 lines up with the 8:45 rule exactly.
 * Horizontal position comes from <c>assignColumns</c>, because two overlapping
 * items in a teacher's column must sit beside each other — that overlap is the
 * thing this view exists to show.
 */
function Block({
  item,
  gridStartMinutes,
  slotMinutes,
  onOpen,
}: {
  item: TeacherItem & { column: number; columnCount: number }
  gridStartMinutes: number
  slotMinutes: number
  onOpen: (kind: 'class' | 'booking', id: string) => void
}) {
  const start = minutesOf(item.startTime)
  const end = minutesOf(item.endTime)

  const rowStart = Math.floor((start - gridStartMinutes) / slotMinutes) + 1
  const rowEnd = Math.max(rowStart + 1, Math.ceil((end - gridStartMinutes) / slotMinutes) + 1)
  const slots = rowEnd - rowStart

  const width = 100 / item.columnCount
  const colour = item.roomColor ?? FALLBACK_COLOUR

  return (
    <button
      onClick={() => onOpen(item.kind, item.sourceId)}
      title={`${item.label} - ${formatTime(item.startTime)}${meridiem(item.startTime)} to ${formatTime(item.endTime)}${meridiem(item.endTime)}${item.roomName ? ` in ${item.roomName}` : ''} - click for the roster`}
      className="room-tint absolute overflow-hidden rounded-md px-1.5 py-0.5 text-left transition-[filter] hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
      style={
        {
          gridRow: `${rowStart} / ${rowEnd}`,
          top: `${(rowStart - 1) * ROW_HEIGHT}px`,
          height: `${slots * ROW_HEIGHT - 2}px`,
          left: `calc(${item.column * width}% + 1px)`,
          width: `calc(${width}% - 2px)`,
          '--room-color': colour,

          // Held-but-empty reads as an outline; booked reads as a solid block.
          background: item.isOpen ? 'transparent' : 'var(--room-fill)',
          border: item.isOpen
            ? '1px dashed var(--room-display)'
            : '1px solid var(--room-display)',
          boxShadow: item.clashes ? '0 0 0 2px var(--color-crit)' : undefined,
        } as React.CSSProperties
      }
    >
      <div className="truncate text-[10.5px] leading-tight font-semibold" style={{ color: 'var(--room-display)' }}>
        {item.label}
      </div>

      {/* Only when the block is tall enough to carry it. */}
      {slots >= 2 && item.roomName && (
        <div className="truncate text-[10px] leading-tight text-ink-3">{item.roomName}</div>
      )}
    </button>
  )
}

function TeacherGrid({
  lanes,
  gridStart,
  gridEnd,
  slotMinutes,
  onOpen,
}: {
  lanes: TeacherLane[]
  gridStart: number
  gridEnd: number
  slotMinutes: number
  onOpen: (kind: 'class' | 'booking', id: string) => void
}) {
  const slotCount = Math.max(1, (gridEnd - gridStart) / slotMinutes)
  const slots = Array.from({ length: slotCount }, (_, i) => gridStart + i * slotMinutes)

  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-surface">
      <div
        className="grid min-w-[820px]"
        style={{ gridTemplateColumns: `62px repeat(${lanes.length}, minmax(130px, 1fr))` }}
      >
        {/* Header row */}
        <div className="sticky left-0 z-10 border-b border-line bg-surface-2" />
        {lanes.map((lane) => {
          const clashes = lane.items.some((i) => i.clashes)
          return (
            <div
              key={lane.staffId}
              className={`border-b border-l border-line px-2 py-2 text-center ${
                clashes ? 'bg-crit-bg' : 'bg-surface-2'
              }`}
            >
              <div className="flex items-center justify-center gap-1 text-[12px] font-semibold">
                {clashes && <AlertTriangle size={12} strokeWidth={2.5} className="shrink-0 text-crit" />}
                <span className="truncate">{lane.staffName}</span>
              </div>
              <div className="text-[10.5px] text-ink-3">
                {lane.items.length} {lane.items.length === 1 ? 'session' : 'sessions'}
              </div>
            </div>
          )
        })}

        {/* Time gutter. Every slot is a row; only the hour gets a label. */}
        <div
          className="sticky left-0 z-10 grid bg-surface-2"
          style={{ gridTemplateRows: `repeat(${slotCount}, ${ROW_HEIGHT}px)` }}
        >
          {slots.map((minutes) => {
            const isHour = minutes % 60 === 0
            const h = Math.floor(minutes / 60)
            const label = `${h % 12 === 0 ? 12 : h % 12}${h >= 12 ? 'pm' : 'am'}`

            return (
              <div
                key={minutes}
                className={`tabular pr-2 text-right text-[10px] ${
                  isHour ? 'font-semibold text-ink-2' : 'text-ink-3'
                }`}
                style={{ borderTop: `1px solid var(--color-${isHour ? 'line-2' : 'line'})` }}
              >
                {isHour ? label : ''}
              </div>
            )
          })}
        </div>

        {/* One column per teacher, over the same slot rules as the room grid. */}
        {lanes.map((lane) => (
          <div key={lane.staffId} className="relative border-l border-line">
            <div
              className="pointer-events-none absolute inset-0 grid"
              style={{ gridTemplateRows: `repeat(${slotCount}, ${ROW_HEIGHT}px)` }}
            >
              {slots.map((minutes) => (
                <div
                  key={minutes}
                  style={{
                    borderTop: `1px solid var(--color-${minutes % 60 === 0 ? 'line-2' : 'line'})`,
                  }}
                />
              ))}
            </div>

            <div className="relative" style={{ height: slotCount * ROW_HEIGHT }}>
              {assignColumns(lane.items).map((item) => (
                <Block
                  key={item.id}
                  item={item}
                  gridStartMinutes={gridStart}
                  slotMinutes={slotMinutes}
                  onOpen={onOpen}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function TeacherDayView({
  initialDate,
  onView,
}: {
  /** Supplied when embedded, since there is no route parameter there (D-312). */
  initialDate?: string
  onView?: (view: CalendarView, date: string) => void
} = {}) {
  const { date: routeDate } = useParams<{ date: string }>()
  const date = initialDate ?? routeDate
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const target = date ?? today()

  const { data: day, isPending } = useQuery({
    queryKey: ['calendar', 'day', target, {}],
    queryFn: () => fetchDay(target, {}),
  })

  const { data: bookings } = useQuery({
    queryKey: ['bookings', target],
    queryFn: () => fetchBookings(target, target),
  })

  const { data: conflicts } = useQuery({
    queryKey: ['conflicts', target],
    queryFn: () => fetchConflicts(target, target),
  })

  const [q, setQ] = useState('')
  const [onlyConflicts, setOnlyConflicts] = useState(false)
  const [staffIds, setStaffIds] = useState<string[]>([])
  const [roster, setRoster] = useState<{ kind: 'class' | 'booking'; id: string } | null>(null)

  /*
   * A verdict given on the conflicts screen has to quiet this one too (D-150).
   *
   * Otherwise Tonya marks an arrangement as deliberate and it carries on being
   * reported in red here — which teaches her that answering the question
   * changes nothing, and the review mechanism is then worse than none.
   *
   * Only "expected" is silenced. Anything she flagged as needing fixing stays
   * exactly where a real double-booking would be.
   */
  const accepted = new Set(
    (conflicts ?? []).filter((c) => c.verdict === 'Expected').map((c) => c.key),
  )
  const unresolved = (conflicts ?? []).filter((c) => c.verdict !== 'Expected')

  /*
   * A private lesson is one item in its teacher's lane, as its booking (D-733).
   * Drawn as both it was two items at the same minute in the same lane, and
   * so a red clash with itself on every makeup private. Paired against the
   * bookings the lanes draw, which leave a cancelled one out.
   */
  const drawn = (bookings ?? []).filter((b) => !bookingIsOff(b))
  const allLanes = buildLanes(bookings ?? [], withoutShownPrivates(day?.entries ?? [], drawn), accepted)
  const lanes = filterLanes(allLanes, { q, onlyConflicts, staffIds })

  const toggleStaff = (id: string) =>
    setStaffIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    )
  const hidden = allLanes.length - lanes.length
  const conflictedCount = allLanes.filter((l) => l.items.some((i) => i.clashes)).length

  // Same window and slot size as the room grid, so switching between the two
  // views of one day does not rescale the day underneath you.
  const { start: gridStart, end: gridEnd } = stretchGrid(
    day ? toMinutes(day.gridStart) : 15 * 60,
    day ? toMinutes(day.gridEnd) : 21 * 60,
    bookings ?? [],
  )
  const slotMinutes = day?.slotMinutes ?? 15

  /*
   * Every move within this view, in one place (D-312). Embedded in the teacher
   * portal there is no route to go to, and /calendar/teachers is gated on
   * ViewAllSchedules anyway.
   */
  const goTeachers = (date: string) => {
    if (onView) return onView("teachers", date)
    navigate(withFilter(`/calendar/teachers/${date}`, searchParams))
  }

  /*
   * The same move for a change of view, now that the switcher has no fallback
   * of its own (D-395).
   *
   * `CalendarViewSwitcher` used to navigate here whenever a caller left
   * `onSelect` off — inside a control that renders three buttons and cannot
   * know whether its reader may open `/calendar/*`. It is written here for the
   * reason `goTeachers` above is: this screen knows which routes it means, and
   * knows an embedded caller answers instead.
   */
  const changeView = (view: CalendarView) => {
    if (onView) return onView(view, target)

    const path = view === 'month' ? '/calendar/month' : `/calendar/${view}/${target}`
    navigate(withFilter(path, searchParams))
  }

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">By teacher</p>
          <CalendarDatePicker
            label={formatFullDate(target)}
            anchor={target}
            view="day"
            onPick={goTeachers}
          />
          <p className="mt-1 text-sm text-ink-3">
            {lanes.length} {lanes.length === 1 ? 'teacher' : 'teachers'} scheduled
            {unresolved.length > 0 && (
              <span className="font-semibold text-crit">
                {' '}
                &middot; {unresolved.length} conflict{unresolved.length === 1 ? '' : 's'}
              </span>
            )}
            {accepted.size > 0 && (
              <span> &middot; {accepted.size} marked as expected</span>
            )}
          </p>
        </div>

        <nav className="flex items-center gap-2">
          <CalendarViewSwitcher
            current="teachers"
            onSelect={changeView}
          />
          <span className="mx-1 h-6 w-px bg-line" aria-hidden="true" />
          <button
            onClick={() => goTeachers(addDays(target, -1))}
            aria-label="Previous day"
            className="rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
          >
            &larr;
          </button>
          <button
            onClick={() => goTeachers(today())}
            className="rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent"
          >
            Today
          </button>
          <button
            onClick={() => goTeachers(addDays(target, 1))}
            aria-label="Next day"
            className="rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
          >
            &rarr;
          </button>
        </nav>
      </header>

      {unresolved.length > 0 && (
        <section className="mb-4 rounded-xl border border-crit bg-crit-bg px-4 py-3">
          <h2 className="flex items-center gap-2 text-[13px] font-bold text-crit">
            <AlertTriangle size={15} strokeWidth={2.5} />
            Double-booked
          </h2>
          <ul className="mt-2 flex flex-col gap-1">
            {unresolved.map((c) => (
              <li key={c.key} className="text-[12.5px] text-crit">
                {c.description}
                {c.verdict === 'NeedsFixing' && (
                  <span className="ml-1.5 font-semibold">&mdash; flagged to fix</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Seventeen teachers is roughly 2,200px of readable columns, so this is
        not a nicety — it is what keeps the view usable at real headcount.
      */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find a teacher"
          aria-label="Filter by teacher name"
          className="w-52 rounded-lg border border-line-2 bg-surface px-3 py-1.5 text-[13px] outline-none placeholder:text-ink-3 focus:border-accent"
        />

        <button
          onClick={() => setOnlyConflicts((v) => !v)}
          aria-pressed={onlyConflicts}
          disabled={conflictedCount === 0 && !onlyConflicts}
          className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
            onlyConflicts
              ? 'border-crit bg-crit-bg text-crit'
              : 'border-line-2 bg-surface text-ink-2 hover:border-ink-3'
          }`}
        >
          <AlertTriangle size={13} strokeWidth={2.5} />
          Only conflicts
          {conflictedCount > 0 && <span className="tabular">({conflictedCount})</span>}
        </button>

        {hidden > 0 && (
          <span className="text-[12.5px] text-ink-3">
            {hidden} hidden
            <button
              onClick={() => {
                setQ('')
                setOnlyConflicts(false)
                setStaffIds([])
              }}
              className="ml-2 font-semibold text-accent hover:underline"
            >
              Show all
            </button>
          </span>
        )}
      </div>

      {/*
        Pick specific teachers. Chips rather than a multi-select control: the
        set is small enough to show at once, and seeing who is scheduled today
        is itself useful before choosing any of them.
      */}
      {allLanes.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          {allLanes.map((lane) => {
            const on = staffIds.includes(lane.staffId)
            return (
              <button
                key={lane.staffId}
                onClick={() => toggleStaff(lane.staffId)}
                aria-pressed={on}
                className={`rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${
                  on
                    ? 'border-accent bg-accent-soft text-accent-2'
                    : 'border-line-2 bg-surface text-ink-2 hover:border-ink-3'
                }`}
              >
                {lane.staffName}
              </button>
            )
          })}
        </div>
      )}

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}

      {!isPending && lanes.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-4 py-10 text-center text-sm text-ink-3">
          {allLanes.length === 0
            ? 'Nobody is scheduled on this day.'
            : 'No teacher matches that filter.'}
        </p>
      ) : (
        lanes.length > 0 && (
          <TeacherGrid
            lanes={lanes}
            gridStart={gridStart}
            gridEnd={gridEnd}
            slotMinutes={slotMinutes}
            onOpen={(kind, id) => {
              // A private's item is the lesson: it opens her attendance (D-733).
              const lesson = kind === 'booking' ? lessonSessionOf(drawn.find((b) => b.id === id) ?? {}) : null
              setRoster(lesson ? { kind: 'class', id: lesson } : { kind, id })
            }}
          />
        )
      )}

      <p className="mt-3 text-[12px] text-ink-3">
        Click any block to see who is in it. Solid blocks are booked; dashed outlines are slots a
        teacher is holding with nobody in them yet. A red edge means that teacher is in two places
        at once.
      </p>

      {roster && (
        <RosterModal
          kind={roster.kind}
          id={roster.id}
          onClose={() => setRoster(null)}
          onOpenSession={(id) => setRoster({ kind: 'class', id })}
        />
      )}
    </div>
  )
}
